
import qs from 'qs'
import { isNotNil } from 'ramda'



export const scrollToAnchor =
  (id: string, timeout = 0) =>
  () => {
    const element = document.getElementById(id)

    if (element) {
      const yOffset = -180

      setTimeout(() => {
        const y =
          element.getBoundingClientRect().top + window.pageYOffset + yOffset

        window.scrollTo({ top: y, behavior: 'smooth' })
      }, timeout)
    }
  }

export const redirectAndScroll = (
  id: string,
  redirectFn: () => Promise<boolean>
) => {
  redirectFn().then(scrollToAnchor(id, 1000)).catch(noop)
}

type ObjectType =
  | { [key: string]: string | object }
  | string
  | object
  | undefined

export const renameKeys = (
  keysMap: keyof ObjectType,
  obj: ObjectType
): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(item => renameKeys(keysMap, item as ObjectType))
  } else if (typeof obj === 'object' && obj !== null) {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const renamedKey = keysMap[key] || key

        acc[renamedKey] = renameKeys(keysMap, obj[key] as keyof ObjectType)

        return acc
      },
      {} as Record<string, unknown>
    )
  }

  return obj
}

export const convertSchemaObjectsToValid = (schema: ObjectType) => {
  if (schema) {
    const newSchemaObject = {
      Product: { ...(schema as { [key: string]: string | object }) },
      BreadcrumbList: schema['breadcrumb'] as { [key: string]: string }
    }

    newSchemaObject.BreadcrumbList['@context'] = 'https://schema.org'
    delete newSchemaObject.Product.breadcrumb

    return newSchemaObject
  } else return
}

export const convertBreadcrumbsForSchemaObject = (
  breadcrumbs: { label: string; path: string | null }[]
) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb.label,
      item: `${process.env.NEXT_PUBLIC_HOME_URL || ''}${breadcrumb.path || ''}`
    }))
  }
}

export const searchParamsToParsedUrlQuery = (
  searchParams: URLSearchParams | null
) => {
  if (!searchParams) return {}

  return qs.parse(searchParams.toString(), { ignoreQueryPrefix: true })
}

export const encodeKeyValue = (
  key: string,
  value: string | boolean | string[]
) => {
  if (Array.isArray(value)) {
    return value
      .map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
      .join('&')
  }

  return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
}

/**
 * Generic polling utility.
 *
 * Keeps calling `getter` until it returns a truthy value or timeout is reached.
 *
 * @template T
 * @param getter - Function that returns the desired value (or null/undefined/false if not ready).
 * @param options - Optional configuration object:
 *   @param options.interval - Time in ms between checks (default: 500).
 *   @param options.timeout - Max wait time in ms before giving up (default: 5000).
 *   @param options.validate - Optional function to validate the returned value
 *                             (default: checks for non-nil and non-empty string).
 * @returns Promise that resolves with the value or `null` if not found in time.
 *
 * @throws Will reject the promise if the `getter` function throws an error during polling.
 */
export function waitForValue<T>(
  getter: () => T,
  options?: {
    interval?: number
    timeout?: number
    validate?: (value: T) => boolean
  }
): Promise<T | null> {
  if (typeof window === 'undefined') return Promise.resolve(null)

  const {
    interval = 500,
    timeout = 5000,
    validate = (value: T) => isNotNil(value) && value !== ''
  } = options || {}

  // `reject` typed to Error so the catch below keeps its assertion (lint).
  return new Promise((resolve, reject: (reason: Error) => void) => {
    const start = Date.now()

    const check = () => {
      try {
        const result = getter()

        if (validate(result)) {
          resolve(result)

          return
        }

        if (Date.now() - start >= timeout) {
          resolve(null)

          return
        }
        setTimeout(check, interval)
      } catch (err: unknown) {
        reject(err as Error)
      }
    }

    check()
  })
}

/**
 * Safely parses a Fetch API Response object to JSON.
 *
 * - Returns `undefined` if the response has no content (respone status 204).
 * - Returns parsed JSON if available and valid.
 * - Returns `undefined` if parsing fails (e.g., invalid JSON or empty body).
 *
 * @template T Expected type of the parsed JSON response.
 * @param response The Fetch API Response object to parse.
 * @returns A promise resolving to the parsed JSON of type `T`, or `undefined` if no valid JSON was found.

 */
export async function safeJson<T = unknown>(
  response: Response
): Promise<T | undefined> {
  if (response.status === 204) {
    return undefined
  }

  try {
    return (await response.json()) as T
  } catch {
    return undefined
  }
}

export const isClient = (): boolean => typeof window !== 'undefined'

/**
 * Remove Polish typography widows ("wdowy") and orphans ("sierotki") in text:
 * - Replaces space after 1–2 letter words with non-breaking space (from predefined list),
 * - Replaces space before the last word with non-breaking space.
 */

// Detects invisible Unicode characters that Sanity stega embeds for visual editing.
// Only BMP characters used here to avoid linter false-positives on astral-plane code points.
const STEGA_DETECT = /[\u200b\u200c\u2062\u2063\u2060\ufeff\u2061]/

export const purgeWidowsAndOrphans = (
  text: string,
  // Glue the last two words together with a non-breaking space so the last
  // line never ends up with a single, lonely word (a "widow"). Disable it
  // for narrow containers where forcing those two words onto one
  // unbreakable line would overflow instead of wrapping.
  preventWidow = true
): string => {
  if (!text) return text

  // Skip typographic transforms when stega encoding is present — modifying the
  // string would corrupt the invisible payload Sanity uses for click-to-edit.
  if (STEGA_DETECT.test(text)) return text

  const orphanWords = new Set([
    'a',
    'i',
    'o',
    'u',
    'w',
    'z',
    'do',
    'na',
    'od',
    'po',
    'za',
    'we',
    'ze'
  ])

  const bounds = text.match(/^(\s*)([\s\S]*?)(\s*)$/)

  if (!bounds) return text
  const [, leading, core, trailing] = bounds

  // Split into alternating word/separator tokens (even indices are words,
  // odd indices are the whitespace runs between them) so both rules can be
  // decided against the same token array in one pass, instead of two
  // sequential regex replacements that can chain into each other.
  const tokens = core.split(/(\s+)/)
  const wordCount = Math.ceil(tokens.length / 2)

  if (wordCount < 2) return text

  const glue = new Array(tokens.length).fill(false)

  // Orphans ("sierotki"): glue 1\u20132 letter connector words to the word after them.
  for (let i = 0; i < tokens.length - 1; i += 2) {
    if (orphanWords.has(tokens[i].toLowerCase())) {
      glue[i + 1] = true
    }
  }

  // Widows ("wdowy"): glue the last word to the one before it.
  if (preventWidow) {
    glue[tokens.length - 2] = true
  }

  const processed = tokens
    .map((token, i) => (glue[i] ? '\u00A0' : token))
    .join('')

  return `${leading}${processed}${trailing}`
}

export const cleanString = (str: string) => {
  return str
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width spaces and joiners
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim()
}

export const toBoolean = (value: string | undefined): boolean => {
  const cleanedValue = value?.trim().toLowerCase() ?? ''

  return !['', 'false', 'null', 'undefined'].includes(cleanedValue)
}

const toCamel = (s: string): string => {
  return s.replace(/([-_][a-z])/gi, $1 => {
    return $1.toUpperCase().replace('-', '').replace('_', '')
  })
}

export const convertKeysToCamelCase = <T>(o: unknown): T => {
  if (Array.isArray(o)) {
    return o.map(v => convertKeysToCamelCase(v)) as T
  } else if (o !== undefined && o !== null && o.constructor === Object) {
    return Object.keys(o).reduce((acc, key) => {
      const camelKey = toCamel(key)

      acc[camelKey] = convertKeysToCamelCase<T[keyof T]>(
        (o as Record<string, unknown>)[key]
      )

      return acc
    }, {} as T)
  }

  return o as T
}

export const mergeRefs = <T>(...refs: Array<React.Ref<T>>) => {
  return (value: T) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(value)
      } else if (ref != null) {
        ref.current = value
      }
    })
  }
}

export const noop = (..._args: unknown[]) => {}

export const isString = (str: unknown) => {
  if (str != null && typeof str.valueOf() === 'string') {
    return true
  }

  return false
}

export const pureHandlePromisesCallbacks = <T, U>(
  fn: (payload: T) => Promise<U | null>,
  payload: T,
  resolve: (value: U | null) => void,
  reject: (error: unknown) => void
) => {
  return fn(payload)
    .then(resolve)
    .catch(reject)
}

export const PRODUCTS_LISTING_LIMIT = 60

export const UPSELLING_LISTING_LIMIT = 50

/**
 * Interleaves two arrays into a single array, alternating elements.
 * @example interleaveArrays([1, 3], [2, 4]) // [1, 2, 3, 4]
 */
export const interleaveArrays = <T>(a: T[], b: T[]): T[] => {
  const result: T[] = []
  const maxLen = Math.max(a.length, b.length)

  for (let i = 0; i < maxLen; i++) {
    if (i < a.length) result.push(a[i])
    if (i < b.length) result.push(b[i])
  }

  return result
}

export const TIME_REVALIDATE_CACHE = 60 * 60 * 24 * 30

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Capitalizes the first letter of each word in a string.
 * Works for uppercase strings, in contrast to CSS `text-transform: capitalize`.
 * @param str - The input string to be transformed.
 */
export const capitalizeWords = (str: string): string => {
  if (!str) return ''

  return str
    .toLowerCase()
    .split(/(\s+|-|')/) // split by space, hyphen, or apostrophe, keep separators
    .map(word => {
      // If it's a separator, return as is
      if (/^\s+$/.test(word) || word === '-' || word === "'" || word === '')
        return word

      // Otherwise, capitalize first char
      return capitalize(word)
    })
    .join('')
}
